import { useEffect, useRef } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

/**
 * Debounce is the parent's responsibility. This is a pure controlled input
 * with a built-in clear button and Esc-to-clear.
 */
export function SearchInput({
  value, onChange, placeholder, className, autoFocus,
}: SearchInputProps) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);

  return (
    <div className={cn(
      'flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm',
      'focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100',
      className,
    )}>
      <SearchIcon className="h-4 w-4 shrink-0 text-slate-400" />
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onChange(''); }}
        placeholder={placeholder ?? 'Search…'}
        className="flex-1 bg-transparent text-sm placeholder:text-slate-400 focus:outline-none"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
